// The words of a triage result (TGCLOUD-1910).
//
// The server sends codes and values, never English
// (backend/services/triage/failureCodes.json). Each is worded here, in the
// clinician's language, through the component's `t`. Nothing from a rule pack
// file is ever put through `t`: the rule, its context, a confirmation, a note,
// TrakGene's interpretation, a `needs` phrase, the attribution and the
// disclaimer are all shown as written. NICE's licence requires its
// recommendations verbatim, a translation is not, and pack text is never sent
// to a translation service at run time (docs/triage/phase-0-findings.md, Q6).
//
// Pure: every function takes `t` (English when omitted) and, where a sentence
// names someone, `nameOf(key)`, which the panel builds from the pedigree it has
// loaded (triagePeople.js). triageText.test.js binds the code lists here to
// failureCodes.json, so a code the engine can return always has words.

import { english } from 'src/i18n/language';

// ── outcomes ───────────────────────────────────────────────────────────────

export const OUTCOMES = Object.freeze([
  'TESTING',
  'REFERRAL',
  'TESTING_AND_REFERRAL',
  'DOES_NOT_MEET'
]);

export const outcomeText = (outcome, t = english) => {
  switch (outcome) {
    case 'TESTING':
      return t('Meets the criteria for genetic testing');
    case 'REFERRAL':
      return t('Meets the criteria for referral');
    case 'TESTING_AND_REFERRAL':
      return t('Meets the criteria for genetic testing and referral');
    case 'DOES_NOT_MEET':
      return t('Does not meet the criteria');
    default:
      return t('Not evaluated');
  }
};

// A pack's outcome in a few words, for its tile in the panel (Tim, QA
// 2026-10-16: the panel says met or not; the full result opens on a click).
export const outcomeShortText = (result, t = english) => {
  if (result?.status === 'refused') return t('Not evaluated here');
  switch (result?.outcome) {
    case 'TESTING':
      return t('Criteria met: testing');
    case 'REFERRAL':
      return t('Criteria met: referral');
    case 'TESTING_AND_REFERRAL':
      return t('Criteria met: testing and referral');
    case 'DOES_NOT_MEET':
      return t('Criteria not met');
    default:
      return t('Not evaluated');
  }
};

// The rule's own outcome, on the line under its wording.
export const ruleOutcomeText = (outcome, t = english) => {
  switch (outcome) {
    case 'TESTING':
      return t('Testing');
    case 'REFERRAL':
      return t('Referral');
    case 'TESTING_AND_REFERRAL':
      return t('Testing and referral');
    default:
      return '';
  }
};

// ── cancers ────────────────────────────────────────────────────────────────

// The concept sets' classes (backend/services/triage/conceptSets.json), bound
// to it by triageText.test.js.
export const CANCER_CLASSES = Object.freeze([
  'breast',
  'ovarian',
  'prostate',
  'pancreatic',
  'colorectal',
  'endometrial',
  'smallBowel',
  'gastric',
  'oesophageal',
  'bladder',
  'ureter',
  'renalPelvis',
  'cholangiocarcinoma',
  'biliary',
  'glioblastoma',
  'brain',
  'endocervical',
  'fallopianTube',
  'primaryPeritoneal',
  'sebaceousCarcinoma',
  'sebaceousAdenoma',
  'keratoacanthoma'
]);

export const cancerText = (key, t = english) => {
  switch (key) {
    case 'breast':
      return t('breast cancer');
    case 'ovarian':
      return t('ovarian cancer');
    case 'prostate':
      return t('prostate cancer');
    case 'pancreatic':
      return t('pancreatic cancer');
    case 'colorectal':
      return t('colorectal cancer');
    case 'endometrial':
      return t('endometrial cancer');
    case 'smallBowel':
      return t('small bowel cancer');
    case 'gastric':
      return t('gastric cancer');
    case 'oesophageal':
      return t('oesophageal cancer');
    case 'bladder':
      return t('bladder cancer');
    case 'ureter':
      return t('ureter cancer');
    case 'renalPelvis':
      return t('renal pelvis cancer');
    case 'cholangiocarcinoma':
      return t('cholangiocarcinoma');
    case 'biliary':
      return t('biliary tract cancer');
    case 'glioblastoma':
      return t('glioblastoma');
    case 'brain':
      return t('brain tumour');
    case 'endocervical':
      return t('endocervical cancer');
    case 'fallopianTube':
      return t('fallopian tube cancer');
    case 'primaryPeritoneal':
      return t('primary peritoneal cancer');
    case 'sebaceousCarcinoma':
      return t('sebaceous carcinoma');
    case 'sebaceousAdenoma':
      return t('sebaceous adenoma');
    case 'keratoacanthoma':
      return t('keratoacanthoma');
    default:
      return String(key || '');
  }
};

const asList = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);

// "breast or ovarian cancer" style lists, joined with the language's own "or".
export const listOr = (items, t = english) => {
  const xs = items.filter(Boolean);
  if (xs.length <= 1) return xs[0] || '';
  if (xs.length === 2) return t('{{a}} or {{b}}', { a: xs[0], b: xs[1] });
  return t('{{list}} or {{last}}', {
    list: xs.slice(0, -1).join(', '),
    last: xs[xs.length - 1]
  });
};

export const listAnd = (items, t = english) => {
  const xs = items.filter(Boolean);
  if (xs.length <= 1) return xs[0] || '';
  if (xs.length === 2) return t('{{a}} and {{b}}', { a: xs[0], b: xs[1] });
  return t('{{list}} and {{last}}', {
    list: xs.slice(0, -1).join(', '),
    last: xs[xs.length - 1]
  });
};

export const cancersText = (keys, t = english) =>
  listOr(
    asList(keys).map((k) => cancerText(k, t)),
    t
  ) || t('the cancers this rule names');

// ── relationships ──────────────────────────────────────────────────────────

export const RELATION_KINDS = Object.freeze([
  'self',
  'parent',
  'grandparent',
  'greatGrandparent',
  'ancestor',
  'child',
  'grandchild',
  'greatGrandchild',
  'descendant',
  'sibling',
  'halfSibling',
  'auntUncle',
  'halfAuntUncle',
  'nieceNephew',
  'halfNieceNephew',
  'firstCousin',
  'halfFirstCousin',
  'greatAuntUncle',
  'halfGreatAuntUncle',
  'grandNieceNephew',
  'halfGrandNieceNephew',
  'firstCousinOnceRemoved',
  'relative'
]);

// One word per kind and sex ('F' | 'M' | 'U'): a translation needs the whole
// word, never an English stem and a suffix.
const RELATION_WORD = {
  self: (sex, t) => t('this person'),
  parent: (sex, t) =>
    sex === 'F' ? t('mother') : sex === 'M' ? t('father') : t('parent'),
  grandparent: (sex, t) =>
    sex === 'F'
      ? t('grandmother')
      : sex === 'M'
      ? t('grandfather')
      : t('grandparent'),
  greatGrandparent: (sex, t) =>
    sex === 'F'
      ? t('great-grandmother')
      : sex === 'M'
      ? t('great-grandfather')
      : t('great-grandparent'),
  child: (sex, t) =>
    sex === 'F' ? t('daughter') : sex === 'M' ? t('son') : t('child'),
  grandchild: (sex, t) =>
    sex === 'F'
      ? t('granddaughter')
      : sex === 'M'
      ? t('grandson')
      : t('grandchild'),
  greatGrandchild: (sex, t) =>
    sex === 'F'
      ? t('great-granddaughter')
      : sex === 'M'
      ? t('great-grandson')
      : t('great-grandchild'),
  sibling: (sex, t) =>
    sex === 'F' ? t('sister') : sex === 'M' ? t('brother') : t('sibling'),
  halfSibling: (sex, t) =>
    sex === 'F'
      ? t('half-sister')
      : sex === 'M'
      ? t('half-brother')
      : t('half-sibling'),
  auntUncle: (sex, t) =>
    sex === 'F' ? t('aunt') : sex === 'M' ? t('uncle') : t('aunt or uncle'),
  halfAuntUncle: (sex, t) =>
    sex === 'F'
      ? t('half-aunt')
      : sex === 'M'
      ? t('half-uncle')
      : t('half-aunt or half-uncle'),
  nieceNephew: (sex, t) =>
    sex === 'F' ? t('niece') : sex === 'M' ? t('nephew') : t('niece or nephew'),
  halfNieceNephew: (sex, t) =>
    sex === 'F'
      ? t('half-niece')
      : sex === 'M'
      ? t('half-nephew')
      : t('half-niece or half-nephew'),
  firstCousin: (sex, t) => t('first cousin'),
  halfFirstCousin: (sex, t) => t('half first cousin'),
  greatAuntUncle: (sex, t) =>
    sex === 'F'
      ? t('great-aunt')
      : sex === 'M'
      ? t('great-uncle')
      : t('great-aunt or great-uncle'),
  grandNieceNephew: (sex, t) =>
    sex === 'F'
      ? t('grandniece')
      : sex === 'M'
      ? t('grandnephew')
      : t('grandniece or grandnephew'),
  halfGreatAuntUncle: (sex, t) =>
    sex === 'F'
      ? t('half-great-aunt')
      : sex === 'M'
      ? t('half-great-uncle')
      : t('half-great-aunt or half-great-uncle'),
  halfGrandNieceNephew: (sex, t) =>
    sex === 'F'
      ? t('half-grandniece')
      : sex === 'M'
      ? t('half-grandnephew')
      : t('half-grandniece or half-grandnephew'),
  firstCousinOnceRemoved: (sex, t) => t('first cousin once removed'),
  ancestor: (sex, t) => t('ancestor'),
  descendant: (sex, t) => t('descendant')
};

// The kinds with a word of their own; any other reads as its degree.
export const RELATION_WORD_KINDS = Object.freeze(Object.keys(RELATION_WORD));

export const degreeText = (degree, t = english) => {
  switch (degree) {
    case 1:
      return t('first-degree relative');
    case 2:
      return t('second-degree relative');
    case 3:
      return t('third-degree relative');
    default:
      return Number.isFinite(degree)
        ? t('relative of degree {{degree}}', { degree })
        : t('relative');
  }
};

/**
 * "maternal aunt", "brother", "first cousin, paternal side".
 *
 * @param {{ kind, degree, side: string[] }|null} relation the engine's
 * @param {'F'|'M'|'U'} sex
 */
export const relationText = (relation, sex, t = english) => {
  if (!relation) return t('not connected on the pedigree');
  const word = RELATION_WORD[relation.kind];
  const base = word ? word(sex, t) : degreeText(relation.degree, t);
  if (relation.kind === 'self') return base;
  const side = asList(relation.side);
  // The person's own descendants and full siblings are on both sides; a side
  // is worth naming only when it is one.
  if (side.length === 1) {
    return side[0] === 'maternal'
      ? t('{{relation}}, maternal side', { relation: base })
      : t('{{relation}}, paternal side', { relation: base });
  }
  return base;
};

export const sideText = (side, t = english) =>
  side === 'maternal'
    ? t('the maternal side')
    : side === 'paternal'
    ? t('the paternal side')
    : t('the family');

const sexWord = (sex, t) =>
  sex === 'F' ? t('female') : sex === 'M' ? t('male') : t('not recorded');

const sexRequiredWord = (sex, t) =>
  sex === 'F' ? t('a woman') : sex === 'M' ? t('a man') : t('anyone');

// ── failures ───────────────────────────────────────────────────────────────

const comparatorText = (comparator, limit, t) => {
  switch (comparator) {
    case 'atMost':
      return t('{{limit}} or younger', { limit });
    case 'atLeast':
      return t('{{limit}} or older', { limit });
    default:
      return t('under {{limit}}', { limit });
  }
};

const peopleText = (keys, nameOf, t) =>
  listAnd(
    asList(keys).map((k) => nameOf(k)),
    t
  );

const allowedText = (allowed, t) => {
  if (allowed === 'sibling') return t('a sibling');
  if (allowed === 'fullSibling') return t('a full sibling');
  const degrees = asList(allowed).filter((d) => Number.isFinite(d));
  if (allowed === 'any' || !degrees.length) return t('a blood relative');
  return listOr(
    degrees.map((d) => degreeText(d, t)),
    t
  );
};

const measureText = (measure, t) => {
  switch (measure) {
    case 'carrierProbability':
      return t('carrier probability');
    case 'breastTenYear':
      return t('10-year breast cancer risk');
    case 'breastLifetime':
      return t('lifetime breast cancer risk');
    default:
      return t('risk estimate');
  }
};

const failureWords = {
  COUNT: (v, { t }) =>
    t(
      '{{found}} relatives with {{cancer}} recorded, the rule requires {{required}}',
      {
        found: v.found ?? 0,
        required: v.required ?? 1,
        cancer: cancersText(v.cancer, t)
      }
    ),
  AGE: (v, { t, nameOf }) =>
    v.age === null || v.age === undefined
      ? t('{{person}}: age at diagnosis does not meet {{limit}}', {
          person: nameOf(v.person),
          limit: comparatorText(v.comparator, v.limit, t)
        })
      : t('{{person}} was diagnosed at {{age}}, the rule requires {{limit}}', {
          person: nameOf(v.person),
          age: v.age,
          limit: comparatorText(v.comparator, v.limit, t)
        }),
  ANY_AGE: (v, { t }) =>
    v.age === null || v.age === undefined
      ? t('nobody was diagnosed under {{limit}}', { limit: v.limit })
      : t(
          'youngest age at diagnosis {{age}}, the rule requires one under {{limit}}',
          { age: v.age, limit: v.limit }
        ),
  FIRST_PRIMARY_AGE: (v, { t, nameOf }) =>
    t(
      "{{person}}'s first breast cancer was at {{age}}, the rule requires under {{limit}}",
      { person: nameOf(v.person), age: v.age ?? '?', limit: v.limit }
    ),
  BILATERAL: (v, { t, nameOf }) =>
    t("{{person}}'s breast cancer is not recorded as bilateral", {
      person: nameOf(v.person)
    }),
  DEGREE: (v, { t, nameOf }) =>
    t('{{person}} is a {{degree}}, the rule requires {{allowed}}', {
      person: nameOf(v.person),
      degree: degreeText(v.degree, t),
      allowed: allowedText(v.allowed, t)
    }),
  SEX: (v, { t, nameOf }) =>
    t('{{person}} is {{sex}}, the rule requires {{required}}', {
      person: nameOf(v.person),
      sex: sexWord(v.sex, t),
      required: sexRequiredWord(v.required, t)
    }),
  FIRST_DEGREE: (v, { t }) =>
    t(
      'none of the relatives is a first-degree relative, the rule requires {{required}}',
      { required: v.required ?? 1 }
    ),
  AVERAGE_AGE: (v, { t }) =>
    t(
      'average age at diagnosis {{average}}, the rule requires under {{limit}}',
      {
        average: v.average,
        limit: v.limit
      }
    ),
  NO_BILATERAL: (v, { t }) =>
    t('no relative is recorded with bilateral breast cancer'),
  INDEX_NO_CANCER: (v, { t }) =>
    t('no {{cancer}} is recorded for this person', {
      cancer: cancersText(v.cancer, t)
    }),
  INDEX_SEX: (v, { t }) =>
    t('this person is {{sex}}, the criterion is for {{required}}', {
      sex: sexWord(v.sex, t),
      required: sexRequiredWord(v.required, t)
    }),
  INDEX_AGE: (v, { t }) =>
    v.age === null || v.age === undefined
      ? t('this person was not diagnosed {{limit}}', {
          limit: comparatorText(v.comparator, v.limit, t)
        })
      : t(
          'this person was diagnosed at {{age}}, the criterion requires {{limit}}',
          {
            age: v.age,
            limit: comparatorText(v.comparator, v.limit, t)
          }
        ),
  INDEX_ISOLATED: (v, { t }) =>
    t(
      "this person's only {{cancer}} is one the criterion does not count on its own",
      { cancer: cancersText(v.cancer, t) }
    ),
  INDEX_EVENTS: (v, { t }) =>
    t(
      '{{found}} of the {{required}} cancers the criterion requires are recorded for this person',
      { found: v.found ?? 0, required: v.required ?? 1 }
    ),
  INDEX_INCLUDES: (v, { t }) =>
    t("none of this person's cancers is {{cancer}}", {
      cancer: cancersText(v.cancer, t)
    }),
  INDEX_NOT_BILATERAL: (v, { t }) =>
    t("this person's breast cancer is not recorded as bilateral"),
  NOT_TRIPLE_NEGATIVE: (v, { t }) =>
    t(
      'receptor status ER {{er}}, PR {{pr}}, HER2 {{her2}} is not triple negative',
      { er: v.er ?? '?', pr: v.pr ?? '?', her2: v.her2 ?? '?' }
    ),
  INDEX_SCOPE: (v, { t }) => {
    if (v.alive === true) return t('the criterion is for a living person');
    if (v.alive === false)
      return t('the criterion is for someone who has died');
    if (v.sex)
      return t('the criterion is for {{required}}', {
        required: sexRequiredWord(v.sex, t)
      });
    if (v.hasCancer)
      return t('the criterion is for someone with {{cancer}}', {
        cancer: cancersText(v.hasCancer, t)
      });
    if (v.hasNoCancer)
      return t('the criterion is for someone without {{cancer}}', {
        cancer: cancersText(v.hasNoCancer, t)
      });
    return t('the criterion is for someone else');
  },
  NO_ANCHOR: (v, { t }) =>
    v.sex === 'F'
      ? t('no female relative with {{cancer}}', {
          cancer: cancersText(v.cancer, t)
        })
      : v.sex === 'M'
      ? t('no male relative with {{cancer}}', {
          cancer: cancersText(v.cancer, t)
        })
      : t('no relative with {{cancer}}', { cancer: cancersText(v.cancer, t) }),
  SIDE: (v, { t, nameOf }) =>
    t('{{people}} are on the other side of the family from {{anchor}}', {
      people: peopleText(v.people, nameOf, t),
      anchor: nameOf(v.anchor)
    }),
  CASES: (v, { t }) =>
    t(
      '{{found}} affected relatives on {{side}}, the criteria require {{required}}',
      { found: v.found ?? 0, required: v.required, side: sideText(v.side, t) }
    ),
  FDR_LINK: (v, { t }) =>
    t(
      'none of the affected relatives is a first-degree relative of the other two'
    ),
  GENERATIONS: (v, { t }) =>
    v.successive
      ? t(
          'the affected relatives span {{found}} successive generations, the criteria require {{required}}',
          { found: v.found ?? 0, required: v.required }
        )
      : t(
          'the affected relatives span {{found}} generations, the criteria require {{required}}',
          { found: v.found ?? 0, required: v.required }
        ),
  YOUNGEST_AGE: (v, { t }) =>
    v.age === null || v.age === undefined
      ? t('nobody affected was diagnosed under {{limit}}', { limit: v.limit })
      : t(
          'youngest age at diagnosis {{age}}, the criteria require one under {{limit}}',
          { age: v.age, limit: v.limit }
        ),
  CLUSTER: (v, { t }) =>
    t('{{count}} of the criteria are not met on {{side}}', {
      count: asList(v.failed).length,
      side: sideText(v.side, t)
    }),
  ONE_OF: (v, { t }) =>
    t('none of the cancers recorded is {{cancer}}', {
      cancer: cancersText(v.cancer, t)
    }),
  INTERVENING: (v, { t, nameOf }) =>
    t(
      '{{intervening}}, between this person and {{person}}, is a living woman',
      { intervening: nameOf(v.intervening), person: nameOf(v.person) }
    ),
  RISK_SCORE: (v, { t }) =>
    v.comparator === 'atLeast'
      ? t('{{measure}} {{value}}%, the criterion requires {{limit}}% or more', {
          measure: measureText(v.measure, t),
          value: v.value,
          limit: v.limit
        })
      : t('{{measure}} {{value}}%, the criterion requires over {{limit}}%', {
          measure: measureText(v.measure, t),
          value: v.value,
          limit: v.limit
        }),
  ANCESTRY: (v, { t }) =>
    v.recorded === 'jewish'
      ? t('Jewish ancestry is recorded, not Ashkenazi Jewish ancestry')
      : t('Ashkenazi Jewish ancestry is not recorded')
};

export const FAILURE_CODES = Object.freeze(Object.keys(failureWords));

/**
 * "average age at diagnosis 62, the rule requires under 60".
 *
 * @param {{ code: string, values: object }} failure
 * @param {{ t?: Function, nameOf?: Function }} [o]
 */
export const failureText = (failure, { t = english, nameOf } = {}) => {
  const words = failureWords[failure?.code];
  const name = nameOf || (() => t('a relative'));
  if (!words) return t('a criterion is not met');
  return words(failure.values || {}, { t, nameOf: name });
};

// ── what a rule needs ─────────────────────────────────────────────────────

const needWords = {
  NEEDS_AGE: (v, { t, nameOf }) =>
    t('age at diagnosis for {{people}}', {
      people: peopleText(v.people, nameOf, t)
    }),
  NEEDS_SEX: (v, { t, nameOf }) =>
    t('sex for {{people}}', { people: peopleText(v.people, nameOf, t) }),
  NEEDS_LATERALITY: (v, { t, nameOf }) =>
    t('which breast was affected, for {{people}}', {
      people: peopleText(v.people, nameOf, t)
    }),
  NEEDS_SECOND_PRIMARY_AGE: (v, { t, nameOf }) =>
    t('age at the second breast cancer for {{people}}', {
      people: peopleText(v.people, nameOf, t)
    }),
  NEEDS_RECEPTOR_STATUS: (v, { t, nameOf }) =>
    t('receptor status (ER, PR, HER2) for {{people}}', {
      people: peopleText(v.people, nameOf, t)
    }),
  NEEDS_INTERVENING: (v, { t, nameOf }) =>
    t('the relative between this person and {{people}}', {
      people: peopleText(v.people, nameOf, t)
    }),
  NEEDS_CANRISK: (v, { t }) =>
    t('a CanRisk result for this person ({{measure}})', {
      measure: measureText(v.measure, t)
    }),
  NEEDS_ANCESTRY: (v, { t }) =>
    t('whether this person has Ashkenazi Jewish ancestry'),
  // The pack's own phrase for what the record cannot hold. Like everything in a
  // pack file it is shown as written: pack text is never sent to be translated.
  NEEDS_DATA: (v, { t }) =>
    v.needs ? String(v.needs) : t('information the record does not hold')
};

export const NEED_CODES = Object.freeze(Object.keys(needWords));

export const needText = (need, { t = english, nameOf } = {}) => {
  const words = needWords[need?.code];
  const name = nameOf || (() => t('a relative'));
  if (!words) return t('information the record does not hold');
  return words(need.values || {}, { t, nameOf: name });
};

// ── refusals ───────────────────────────────────────────────────────────────

// A territory's name in the clinician's language, from the browser.
export const territoryName = (code, lang) => {
  if (!code) return '';
  try {
    const names = new Intl.DisplayNames([lang || 'en'], { type: 'region' });
    return names.of(String(code).toUpperCase()) || String(code);
  } catch (_) {
    return String(code);
  }
};

const refusalWords = {
  TERRITORY_NOT_DECLARED: (v, { t, pack, lang }) =>
    t(
      'The licence for {{pack}} covers use in these countries only: {{territories}}. This service has not said which country it is in, so the pack has not been evaluated. A System Admin or Genetics Service Admin sets the country in Settings, Triage.',
      {
        pack,
        territories: listAnd(
          asList(v.territories).map((c) => territoryName(c, lang)),
          t
        )
      }
    ),
  TERRITORY_OUTSIDE: (v, { t, pack, lang }) =>
    t(
      'The licence for {{pack}} covers use in these countries only: {{territories}}. This service is set to {{territory}}, so the pack has not been evaluated.',
      {
        pack,
        territory: territoryName(v.territory, lang),
        territories: listAnd(
          asList(v.territories).map((c) => territoryName(c, lang)),
          t
        )
      }
    ),
  INDEX_NOT_FOUND: (v, { t }) => t('This person is not on the pedigree.')
};

export const REFUSAL_CODES = Object.freeze(Object.keys(refusalWords));

export const refusalText = (refusal, { t = english, pack = '', lang } = {}) => {
  const words = refusalWords[refusal?.code];
  if (!words) return t('This rule pack could not be evaluated.');
  return words(refusal.values || {}, { t, pack, lang });
};

// ── data notes ─────────────────────────────────────────────────────────────

const uncountedReasonText = (reason, t) => {
  switch (reason) {
    case 'syndrome':
      return t('a syndrome, not the cancer itself');
    case 'family-history':
      return t('a family history, not a diagnosis');
    case 'code-not-in-set':
      return t('its code is not in the counted set');
    case 'snomed-not-reviewed':
      return t('its SNOMED CT code has not been reviewed for counting');
    case 'in-situ-or-secondary':
      return t('in situ or secondary');
    case 'other-disease':
      return t('recorded as Other Disease/Comment');
    case 'status':
      return t('its status does not say it is present');
    default:
      return t('not counted');
  }
};

export const UNCOUNTED_REASONS = Object.freeze([
  'syndrome',
  'family-history',
  'code-not-in-set',
  'snomed-not-reviewed',
  'in-situ-or-secondary',
  'other-disease',
  'status'
]);

const dataNoteWords = {
  // `name` is the diagnosis as recorded and `ageText` the age as typed: values,
  // filled in after the lookup, never sent to be translated.
  AGE_UNREADABLE: (v, { t, nameOf }) => [
    t(
      '{{person}}: the age at diagnosis of {{diagnosis}} reads "{{age}}", which is not a number, so it was not counted',
      {
        person: nameOf(v.person),
        diagnosis: v.name || t('a cancer'),
        age: v.ageText
      }
    )
  ],
  UNCOUNTED: (v, { t, nameOf }) =>
    asList(v.diagnoses).map((d) =>
      t('{{person}}: {{diagnosis}} was not counted ({{reason}})', {
        person: nameOf(d.person),
        diagnosis: d.name || d.codeId || t('a diagnosis'),
        reason: uncountedReasonText(d.reason, t)
      })
    ),
  UNPLACED: (v, { t, nameOf }) => [
    t(
      '{{people}}: a counted cancer is recorded, but they are not connected on the pedigree, so they were not counted',
      { people: peopleText(v.people, nameOf, t) }
    )
  ],
  NO_SAVED_PEDIGREE: (v, { t }) => [
    t(
      'This family has no saved pedigree. Relationships were read from the relationship labels, so check the result against the chart.'
    )
  ]
};

export const DATA_NOTE_CODES = Object.freeze(Object.keys(dataNoteWords));

/** A data note can name several diagnoses: one line each. */
export const dataNoteLines = (note, { t = english, nameOf } = {}) => {
  const words = dataNoteWords[note?.code];
  const name = nameOf || (() => t('a relative'));
  if (!words) return [];
  return words(note.values || {}, { t, nameOf: name });
};

// ── a relative's diagnoses, for the satisfying relatives list ──────────────

/**
 * "breast cancer at 45, bilateral". `events` are the engine's (personRef).
 * The diagnosis's recorded name is not used: the class is what the rule
 * counted.
 */
export const eventsText = (events, bilateral, t = english) => {
  const parts = asList(events).map((e) => {
    const cancer = listOr(
      asList(e.classes).map((c) => cancerText(c, t)),
      t
    );
    if (e.age !== null && e.age !== undefined)
      return t('{{cancer}} at {{age}}', { cancer, age: e.age });
    return t('{{cancer}}, age not recorded', { cancer });
  });
  if (bilateral === 'bilateral') parts.push(t('bilateral'));
  return parts.join('; ');
};

/**
 * A stored triage result in one line, for the record tiles and cards:
 * "NICE CG164 2023-11-14: Meets the criteria for referral". The pack's name
 * and version are its own and shown as they are.
 *
 * @param {object} triageResult riskfactors.triageResult
 */
export const triageSummary = (triageResult, t = english) =>
  triageResult
    ? t('{{pack}} {{version}}: {{outcome}}', {
        pack: triageResult.shortName || triageResult.packName || '',
        version: triageResult.packVersion || '',
        outcome: outcomeText(triageResult.outcome, t)
      })
    : '';
